// Email-related types and interfaces

export interface EmailRecipient {
  id: string | number;
  email: string;
  name: string;
  type: 'patient' | 'contact';
  // Optional patient-specific fields (when type is 'patient')
  date_of_birth?: string;
  medical_record_number?: string;
  // Optional contact-specific fields (when type is 'contact') - ALL fields from Contact model
  salutation?: string;
  first_name?: string;
  last_name?: string;
  organization?: string;  // alias for company
  company?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  address_line_1?: string;
  address_line_2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  specialty?: string;
  contact_type_name?: string;
}

export interface EmailAttachment {
  id?: string;
  file?: File;
  file_name: string;
  file_size: number;
  file_type: string;
  file_data?: string; // Base64 encoded file data
  document_id?: string; // Reference to existing patient document
  patient_id?: string; // If from patient documents
  patient_name?: string; // Display name for patient document source
  source?: 'upload' | 'patient_document'; // Track source of attachment
  url?: string;
}

export interface EmailSignature {
  id: string;
  name: string;
  content: string; // HTML content
  is_default: boolean;
  user_id?: string;
  provider_id?: string;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject_template: string;
  body_template: string; // HTML content with variables like [PatientName], [ProviderName], etc.
  description?: string;
  category?: string; // e.g., 'appointment', 'follow-up', 'referral'
  is_active: boolean;
  created_at: string;
  updated_at: string;
  variables?: string[]; // List of available variables
}

export interface Email {
  id: string;
  from_email: string;
  from_name: string;
  to_recipients: EmailRecipient[];
  cc_recipients?: EmailRecipient[];
  bcc_recipients?: EmailRecipient[];
  subject: string;
  body: string; // HTML content
  plain_text_body?: string;
  attachments?: EmailAttachment[];
  signature_id?: string;
  template_id?: string;
  patient_id?: string;
  related_document_id?: string;
  status: 'draft' | 'sent' | 'failed' | 'scheduled';
  sent_at?: string;
  scheduled_at?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  error_message?: string;
}

export interface EmailFormData {
  from_email: string;
  to_recipients: EmailRecipient[];
  cc_recipients: EmailRecipient[];
  bcc_recipients: EmailRecipient[];
  subject: string;
  body: string;
  signature_id: string;
  template_id: string;
  attachments: EmailAttachment[];
  patient_id?: string;
  scheduled_at?: string;
}

export interface EmailSendRequest {
  from_email: string;
  to_recipients: string[]; // Array of email addresses
  cc_recipients?: string[];
  bcc_recipients?: string[];
  subject: string;
  body: string;
  plain_text_body?: string;
  signature_id?: string;
  template_id?: string;
  patient_id?: string;
  attachments?: {
    file_name: string;
    content: string; // Base64 encoded
    content_type: string;
  }[];
  scheduled_at?: string;
}

export interface EmailListParams {
  status?: 'draft' | 'sent' | 'failed' | 'scheduled';
  patient_id?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
}

export interface ClinicEmailSettings {
  id: string;
  default_from_email: string;
  use_clinic_email: boolean; // If true, use clinic email; if false, use user's email
  clinic_email: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_use_tls?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ComposeEmailProps {
  onClose?: () => void;
  prefilledPatient?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  prefilledSubject?: string;
  replyToEmail?: Email;
}
// Email Account types
export type EmailProtocol = 'imap' | 'pop3' | 'exchange' | 'o365';
export type EmailSecurity = 'none' | 'ssl' | 'starttls';

export interface EmailAccount {
  id: string;
  user: number;
  user_display?: string;
  name: string;
  email_address: string;
  protocol: EmailProtocol;
  
  // Incoming mail settings
  incoming_server: string;
  incoming_port: number;
  incoming_security: EmailSecurity;
  
  // Outgoing mail settings (SMTP)
  smtp_server: string;
  smtp_port: number;
  smtp_security: EmailSecurity;
  
  // Authentication
  username: string;
  // Note: password is write-only, never returned from API
  
  // Exchange/O365 specific
  exchange_autodiscover: boolean;
  exchange_server?: string;
  
  // POP3 specific
  leave_messages_on_server: boolean;
  delete_after_days: number;
  
  // General settings
  is_default: boolean;
  is_active: boolean;
  root_folder_path: string;
  
  // Sync settings
  sync_enabled: boolean;
  sync_frequency: number;
  
  // Metadata
  created_at: string;
  updated_at: string;
  last_sync?: string;
  
  // Connection status
  connection_status?: {
    is_configured: boolean;
    last_sync?: string;
  };
}

export interface EmailAccountFormData {
  name: string;
  email_address: string;
  protocol: EmailProtocol;
  
  // Incoming mail settings
  incoming_server: string;
  incoming_port: number;
  incoming_security: EmailSecurity;
  
  // Outgoing mail settings (SMTP)
  smtp_server: string;
  smtp_port: number;
  smtp_security: EmailSecurity;
  
  // Authentication
  username: string;
  password: string;
  
  // Exchange/O365 specific
  exchange_autodiscover: boolean;
  exchange_server?: string;
  
  // POP3 specific
  leave_messages_on_server: boolean;
  delete_after_days: number;
  
  // General settings
  is_default: boolean;
  is_active: boolean;
  root_folder_path: string;
  
  // Sync settings
  sync_enabled: boolean;
  sync_frequency: number;
}

export interface EmailAccountTestResult {
  success: boolean;
  message: string;
}

// Default port configurations for different protocols
export const EMAIL_DEFAULTS = {
  imap: {
    incoming_port: 993,
    incoming_security: 'ssl' as EmailSecurity,
    smtp_port: 587,
    smtp_security: 'starttls' as EmailSecurity,
  },
  pop3: {
    incoming_port: 995,
    incoming_security: 'ssl' as EmailSecurity,
    smtp_port: 587,
    smtp_security: 'starttls' as EmailSecurity,
  },
  exchange: {
    incoming_port: 443,
    incoming_security: 'ssl' as EmailSecurity,
    smtp_port: 587,
    smtp_security: 'starttls' as EmailSecurity,
  },
  o365: {
    incoming_port: 993,
    incoming_security: 'ssl' as EmailSecurity,
    smtp_port: 587,
    smtp_security: 'starttls' as EmailSecurity,
  },
};

// Common email providers configurations
export const EMAIL_PROVIDERS = {
  gmail: {
    name: 'Gmail',
    incoming_server: 'imap.gmail.com',
    smtp_server: 'smtp.gmail.com',
    protocol: 'imap' as EmailProtocol,
  },
  outlook: {
    name: 'Outlook.com',
    incoming_server: 'outlook.office365.com',
    smtp_server: 'smtp.office365.com',
    protocol: 'o365' as EmailProtocol,
  },
  yahoo: {
    name: 'Yahoo Mail',
    incoming_server: 'imap.mail.yahoo.com',
    smtp_server: 'smtp.mail.yahoo.com',
    protocol: 'imap' as EmailProtocol,
  },
};